import { Modal } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormTextInput, FormSubmitButton, GlobalButton } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { brandSchema, BrandSchema, defaultBrandValues } from '../models';
import { useBrandsActions, useBrands } from '../hooks';

interface AddBrandModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (brand: { id: string; name: string }) => void;
}

const AddBrandModal = ({ open, onClose, onSuccess }: AddBrandModalProps) => {
  const { handleSubmit, control, reset, watch } = useForm<BrandSchema>({
    resolver: zodResolver(brandSchema),
    defaultValues: defaultBrandValues,
  });

  const { onCreateBrand, isCreatingBrand } = useBrandsActions();
  const { brands } = useBrands({ pageSize: 1000 });
  
  const brandName = watch('name');
  const existingBrand = brands.find(
    brand => brand.name.toLowerCase() === brandName?.toLowerCase()
  );

  const onSubmit = async (data: BrandSchema) => {
    if (existingBrand) {
      onSuccess({ id: existingBrand.id, name: existingBrand.name });
      onClose();
      return;
    }
    
    try {
      const result = await onCreateBrand(data, reset);
      onSuccess({ id: result.id, name: result.name });
      onClose();
    } catch (error) {
      console.error('Error creating brand:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
        <div className='flex flex-col p-10 gap-4 w-[500px] bg-white outline-none rounded-sm shadow-xl'>
          <div className='flex justify-between items-center'>
            <h2 className='text-xl font-bold'>Agregar nueva marca</h2>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
            <FormTextInput
              name='name'
              placeholder='Nombre de la marca'
              control={control}
              autoFocus
              required
            />
            {existingBrand && brandName && (
              <div className='text-green-600 text-sm'>
                ✓ La marca "{existingBrand.name}" ya existe y será seleccionada
              </div>
            )}
            <div className='flex gap-3 justify-end'>
              <GlobalButton
                variant={GlobalButtonVariant.secondaryButton}
                action={handleClose}
              >
                Cancelar
              </GlobalButton>
              <FormSubmitButton
                label='Agregar marca'
                action={handleSubmit(onSubmit)}
                isLoading={isCreatingBrand}
              />
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default AddBrandModal; 